import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";

function isClosed(insp) {
  // be tolerant while backend evolves
  return insp.status === "completed" || !!insp.completed_at;
}

export default function Inspections() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [tab, setTab] = useState("ongoing"); // "ongoing" | "completed"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await api("/india/inspections?include_drafts=true", { token });
        setItems(res.items || []);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to load inspections");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const { draftList, openList, closedList } = useMemo(() => {
    const draftList = [];
    const openList = [];
    const closedList = [];
    for (const insp of items) {
      if (insp.status === "draft") draftList.push(insp);
      else if (isClosed(insp)) closedList.push(insp);
      else openList.push(insp);
    }
    return { draftList, openList, closedList };
  }, [items]);

  const tabBtn = (key) => ({
    padding: "6px 12px", borderRadius: 6, border: "1px solid #d0dcea",
    fontSize: 13, cursor: "pointer",
    background: tab === key ? "#1e3a5f" : "white",
    color: tab === key ? "white" : "#1e3a5f",
  });

  const listCard = { background: "white", borderRadius: 12, border: "1px solid #d0dcea", overflow: "hidden" };

  return (
    <div style={{ paddingTop: 32, paddingLeft: 24, paddingRight: 24, maxWidth: 960, margin: "0 auto" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Inspections</h1>

        <button
          type="button"
          disabled={creating}
          onClick={async () => {
            setCreateErr("");
            setCreating(true);
            try {
              const res = await api("/india/inspections/draft", { token, method: "POST", body: { location_type: "vessel" } });
              navigate(`/inspections/${res.inspection_id}/setup`);
            } catch (e) {
              setCreateErr(e?.message || "Failed to create inspection");
            } finally {
              setCreating(false);
            }
          }}
          style={{
            padding: "8px 16px", background: "#1e3a5f", color: "white",
            border: "none", borderRadius: 8, fontSize: 14,
            cursor: creating ? "not-allowed" : "pointer", opacity: creating ? 0.5 : 1,
          }}
        >
          {creating ? "Creating…" : "Create inspection"}
        </button>
      </div>

      {createErr && (
        <div style={{
          fontSize: 14, color: "#c0392b", border: "1px solid #fdecea",
          borderRadius: 8, padding: 12, marginBottom: 16, background: "#fdecea",
        }}>{createErr}</div>
      )}

      {loading && <div style={{ fontSize: 14, color: "#6b8cae" }}>Loading…</div>}
      {!loading && err && (
        <div style={{
          fontSize: 14, color: "#c0392b", border: "1px solid #fdecea",
          borderRadius: 8, padding: 12, marginBottom: 16, background: "#fdecea",
        }}>{err}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        <button type="button" onClick={() => setTab("ongoing")} style={tabBtn("ongoing")}>
          Ongoing ({openList.length})
        </button>

        <button type="button" onClick={() => setTab("completed")} style={tabBtn("completed")}>
          Completed ({closedList.length})
        </button>

        <button type="button" onClick={() => setTab("drafts")} style={tabBtn("drafts")}>
          Drafts ({draftList.length})
        </button>
      </div>

      {tab === "ongoing" && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", marginBottom: 8 }}>Ongoing inspections</h2>

          <div style={listCard}>
            {openList.map((insp, idx) => (
              <div key={insp.id} style={{
                padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                borderTop: idx > 0 ? "1px solid #d0dcea" : "none",
              }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: "#1e3a5f" }}>{insp.name || insp.vessel_name || `Inspection ${insp.id}`}</div>
                  <div style={{ fontSize: 13, color: "#6b8cae" }}>{insp.open_findings ?? 0} open points</div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate(`/inspections/${insp.id}`)}
                  style={{
                    padding: "6px 12px", background: "#1e3a5f", color: "white",
                    border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer",
                  }}
                >
                  Open
                </button>
              </div>
            ))}

            {!loading && openList.length === 0 && (
              <div style={{ padding: 12, fontSize: 14, color: "#6b8cae" }}>No ongoing inspections.</div>
            )}
          </div>
        </section>
      )}

      {tab === "completed" && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", marginBottom: 8 }}>Completed inspections</h2>

          <div style={listCard}>
            {closedList.map((insp, idx) => (
              <div key={insp.id} style={{
                padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                borderTop: idx > 0 ? "1px solid #d0dcea" : "none",
              }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: "#1e3a5f" }}>{insp.name || insp.vessel_name || `Inspection ${insp.id}`}</div>
                  <div style={{ fontSize: 13, color: "#6b8cae" }}>Completed</div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate(`/inspections/${insp.id}`)}
                  style={{
                    padding: "6px 12px", border: "1px solid #d0dcea", borderRadius: 6,
                    background: "white", fontSize: 13, cursor: "pointer",
                  }}
                >
                  Open
                </button>
              </div>
            ))}

            {!loading && closedList.length === 0 && (
              <div style={{ padding: 12, fontSize: 14, color: "#6b8cae" }}>No completed inspections.</div>
            )}
          </div>
        </section>
      )}

      {tab === "drafts" && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", marginBottom: 8 }}>Draft inspections</h2>

          <div style={listCard}>
            {draftList.map((insp, idx) => (
              <div key={insp.id} style={{
                padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                borderTop: idx > 0 ? "1px solid #d0dcea" : "none",
              }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: "#1e3a5f" }}>{insp.name || insp.vessel_name || `Draft #${insp.id}`}</div>
                  <div style={{ fontSize: 13, color: "#6b8cae" }}>Not started</div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate(`/inspections/${insp.id}/setup`)}
                  style={{
                    padding: "6px 12px", border: "1px solid #d0dcea", borderRadius: 6,
                    background: "white", fontSize: 13, cursor: "pointer",
                  }}
                >
                  Edit setup
                </button>
              </div>
            ))}

            {!loading && draftList.length === 0 && (
              <div style={{ padding: 12, fontSize: 14, color: "#6b8cae" }}>No draft inspections.</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
